'use client';

/**
 * **The money input's typing behaviour, extracted so two chromes can share it**
 * (LF-6314).
 *
 * A currency `Input` in this codebase is not a plain number field: it renders
 * thousands separators, it holds the user's *raw* keystrokes while focused so a
 * half-typed `1.` or `1.0` is not reformatted out from under them, and it treats a
 * stored `0` as a figure the user recorded rather than as an absent value
 * (LF-6093 — a nil balance is a routine probate outcome).
 *
 * All three of those lived as local helpers inside {@link FormField}. The compact
 * chrome the attribute treatment needs is a *different wrapper around the same
 * behaviour*, and re-typing `formatWithCommas` beside it is exactly the
 * second-definition-of-one-fact the root `CLAUDE.md` forbids — the more so here,
 * because the `0` handling is a decision with a ticket behind it that a re-typed
 * copy would quietly drop.
 */

import { useState } from 'react';

/**
 * `1234.5` → `"1,234.5"`. A stored `0` populates the input as `"0"`, not as an
 * empty field (LF-6093): an empty input reads as "never entered" even when the
 * user opened the form to correct a figure they deliberately recorded as nil.
 */
export function formatWithCommas(num: string | number | undefined): string {
  if (num !== 0 && !num) return '';
  const numStr = String(num);
  const cleaned = numStr.replace(/,/g, '');
  const parts = cleaned.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
}

/** `"1,234.5"` → `1234.5`. Anything unparseable is `0`, matching the schemas. */
export function parseCommaNumber(str: string): number {
  const cleaned = str.replace(/[^0-9.-]/g, '');
  return parseFloat(cleaned) || 0;
}

/**
 * The controlled-input plumbing for a currency field.
 *
 * **Why the raw string is held at all:** formatting on every keystroke turns
 * `12.` into `12` and then refuses the `5`, so a decimal is unenterable. While
 * the field has focus the user's own text is what shows; on blur it is dropped
 * and the formatted value takes over.
 *
 * Call it unconditionally (it is a hook) and use its result only on the currency
 * branch — the same shape {@link FormField} had before the extraction.
 */
export function useCurrencyInput({
  value,
  onChange
}: {
  value: string | number | undefined;
  onChange?: (value: number) => void;
}) {
  const [raw, setRaw] = useState<string | null>(null);
  const [focused, setFocused] = useState(false);

  return {
    /** What the input shows: the user's keystrokes while focused, else formatted. */
    displayValue: focused && raw !== null ? raw : formatWithCommas(value),
    handleChange: (next: string) => {
      setRaw(next);
      onChange?.(parseCommaNumber(next));
    },
    handleFocus: () => {
      setFocused(true);
      setRaw(value?.toString() ?? '');
    },
    handleBlur: () => {
      setFocused(false);
      setRaw(null);
    }
  };
}
